import React, { useState, useEffect } from 'react';
import { BarChart3, MessageCircle, Zap, Server, RefreshCw, Calendar, ArrowRight, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

interface A2AStats {
  total_requests_sent: number;
  total_requests_received: number;
  success_rate: number;
  average_latency: number;
  daily_stats: Array<{
    date: string;
    requests_sent: number;
    requests_received: number;
  }>;
}

interface AgentStats {
  agent_id: string;
  name: string;
  stats: A2AStats;
}

interface UserStats {
  total_agents: number;
  total_messages: number;
  agents: AgentStats[];
}

export function A2AUsageDashboard() {
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [agentStats, setAgentStats] = useState<any | null>(null);
  const [timeRange, setTimeRange] = useState<number>(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUserStats();
    }
  }, [user, timeRange]);

  useEffect(() => {
    if (selectedAgent) {
      fetchAgentStats(selectedAgent);
    }
  }, [selectedAgent, timeRange]);

  const fetchUserStats = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-usage-tracker`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_user_stats',
            user_id: user.id,
            days: timeRange
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user stats');
      }

      const data = await response.json();
      
      if (data.success) {
        setUserStats(data.stats);
        
        // If there are agents and none is selected, select the first one
        if (data.stats.agents.length > 0 && !selectedAgent) {
          setSelectedAgent(data.stats.agents[0].agent_id);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch user stats');
      }
    } catch (err: any) {
      console.error('Error fetching user stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentStats = async (agentId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-usage-tracker`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_agent_stats',
            agent_id: agentId,
            days: timeRange
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch agent stats');
      }

      const data = await response.json();
      
      if (data.success) {
        setAgentStats(data);
      } else {
        throw new Error(data.error || 'Failed to fetch agent stats');
      }
    } catch (err: any) {
      console.error('Error fetching agent stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAgentName = (agentId: string) => {
    if (!userStats) return 'Unknown Agent';
    const agent = userStats.agents.find(a => a.agent_id === agentId);
    return agent ? agent.name : 'Unknown Agent';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">A2A Usage Dashboard</h2>
            <p className="opacity-90">Monitor your Agent-to-Agent communication</p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-8">
        {/* Time Range Selector */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">A2A Protocol Usage</h3>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>
            
            <button
              onClick={() => {
                fetchUserStats();
                if (selectedAgent) {
                  fetchAgentStats(selectedAgent);
                }
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        
        {loading && !userStats ? (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading A2A usage data...</p>
          </div>
        ) : !user ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="font-medium text-gray-900 mb-2">Sign in to view your A2A usage</h4>
            <p className="text-gray-600 mb-4">
              You need to be signed in to view your Agent-to-Agent communication statistics.
            </p>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign In
            </button>
          </div>
        ) : !userStats || userStats.total_agents === 0 ? (
          <div className="text-center py-12">
            <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="font-medium text-gray-900 mb-2">No A2A-enabled Agents</h4>
            <p className="text-gray-600 mb-4">
              You don't have any agents with A2A protocol enabled yet. Create an agent and enable A2A to get started.
            </p>
            <button
              onClick={() => window.location.href = '/create-agent'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Agent
            </button>
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Server className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{userStats.total_agents}</h4>
                    <p className="text-sm text-gray-600">A2A-enabled Agents</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {userStats.agents.slice(0, 3).map((agent) => (
                    <button
                      key={agent.agent_id}
                      onClick={() => setSelectedAgent(agent.agent_id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedAgent === agent.agent_id
                          ? 'bg-blue-200 text-blue-800'
                          : 'hover:bg-blue-100 text-blue-700'
                      }`}
                    >
                      {agent.name}
                    </button>
                  ))}
                  
                  {userStats.agents.length > 3 && (
                    <div className="text-xs text-blue-600 text-center">
                      +{userStats.agents.length - 3} more agents
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <MessageCircle className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{userStats.total_messages}</h4>
                    <p className="text-sm text-gray-600">Total A2A Messages</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-700">Last {timeRange} days</span>
                    <span className="font-medium text-purple-900">{userStats.total_messages} messages</span>
                  </div>
                  
                  <div className="h-10 bg-purple-100 rounded-lg overflow-hidden relative">
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-purple-800 font-medium">
                      A2A Communication Activity
                    </div>
                    {/* Simple activity visualization */}
                    <div className="flex h-full">
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div 
                          key={i}
                          className="h-full bg-purple-500 opacity-70"
                          style={{ 
                            width: `${100/10}%`, 
                            height: `${Math.random() * 100}%`,
                            marginTop: 'auto'
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Zap className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">A2A Performance</h4>
                    <p className="text-sm text-gray-600">System Health</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-green-700">Success Rate</span>
                      <span className="font-medium text-green-900">98.7%</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '98.7%' }}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-green-700">Avg. Latency</span>
                      <span className="font-medium text-green-900">245ms</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-700">System Status</span>
                    <span className="flex items-center gap-1 font-medium text-green-900">
                      <CheckCircle className="w-4 h-4" />
                      Operational
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Agent Details */}
            {selectedAgent && agentStats && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {getAgentName(selectedAgent)} Details
                  </h3>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const currentIndex = userStats.agents.findIndex(a => a.agent_id === selectedAgent);
                        if (currentIndex > 0) {
                          setSelectedAgent(userStats.agents[currentIndex - 1].agent_id);
                        }
                      }}
                      disabled={userStats.agents.findIndex(a => a.agent_id === selectedAgent) === 0}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const currentIndex = userStats.agents.findIndex(a => a.agent_id === selectedAgent);
                        if (currentIndex < userStats.agents.length - 1) {
                          setSelectedAgent(userStats.agents[currentIndex + 1].agent_id);
                        }
                      }}
                      disabled={userStats.agents.findIndex(a => a.agent_id === selectedAgent) === userStats.agents.length - 1}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Agent Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Message Statistics</h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {agentStats.stats.total_requests_sent}
                        </div>
                        <div className="text-sm text-blue-700">Sent</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {agentStats.stats.total_requests_received}
                        </div>
                        <div className="text-sm text-green-700">Received</div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Success Rate</span>
                          <span className="font-medium text-gray-900">{agentStats.stats.success_rate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${agentStats.stats.success_rate}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Average Latency</span>
                          <span className="font-medium text-gray-900">{agentStats.stats.average_latency.toFixed(0)}ms</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(100, agentStats.stats.average_latency / 10)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Top Communication Partners</h4>
                    
                    {agentStats.top_partners && agentStats.top_partners.length > 0 ? (
                      <div className="space-y-3">
                        {agentStats.top_partners.map((partner: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                                <Server className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">Agent {partner.agent_id.substring(0, 8)}...</div>
                                <div className="text-xs text-gray-500">ID: {partner.agent_id}</div>
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              {partner.message_count} messages
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No communication partners yet
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Recent Messages */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Recent Messages</h4>
                  
                  {agentStats.recent_messages && agentStats.recent_messages.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Direction</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {agentStats.recent_messages.map((message: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {message.message_type}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  message.sender_agent_id === selectedAgent
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {message.sender_agent_id === selectedAgent ? 'Outgoing' : 'Incoming'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  message.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : message.status === 'processed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {message.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(message.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No messages yet
                    </div>
                  )}
                </div>
                
                {/* Daily Activity Chart */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Daily Activity</h4>
                  
                  {agentStats.stats.daily_stats && agentStats.stats.daily_stats.length > 0 ? (
                    <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">Interactive chart would be here</p>
                        <p className="text-xs text-gray-400">Showing data for last {timeRange} days</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No daily activity data available
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Documentation Link */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Learn More About A2A</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Check out our comprehensive documentation on implementing and optimizing the A2A protocol.
                </p>
                <a
                  href="/a2a-documentation"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  View A2A Documentation
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}